/**
 * MARPage.jsx
 * CCH Care — Phase 4c
 * Last Modified: 2026-06-10
 *
 * Medication Administration Record — standalone page.
 * Two views: resident list → resident MAR detail.
 * Nurses/Supervisors/Admins can document administration.
 * Medication management (add/edit/discontinue) is role-gated.
 *
 * Changes (2026-06-17) — Deferred co-sign (no-witness) affordance:
 * - AdminModal + PRNModal no longer hard-block a controlled-substance
 *   administration when no witness is selected. A "No eligible witness
 *   available — document & defer" checkbox lets the nurse record the dose
 *   now and leave the co-sign pending; the backend stores it deferred
 *   (witness_id null, cosign_required 1, cosigned_at NULL) to be cleared
 *   by an eligible witness via action=cosign by end of next shift.
 *   Matches the backend policy (soft-nag, never hard-block) — previously
 *   the required Witness field dead-ended lone-licensed coverage (empty
 *   witness list = no path to chart a dose that was actually given).
 *   Selecting a witness and deferring are mutually exclusive; when
 *   deferring, the banner switches to the pending-co-sign consequence and
 *   an empty witness list shows a hint pointing at the defer option.
 *   Frontend-only.
 *
 * Changes (2026-04-17):
 * - Prescribing physician: placeholder clarifies "last name only";
 *   "Dr. " prefix is added automatically at display time (MedicationCard).
 *   Form now strips any leading Dr./Doctor on load and submit to prevent
 *   double-Dr. display ("Dr. Dr. Smith") for legacy data or user habit.
 *
 * Changes (2026-04-20) — Dark mode + ESLint pass:
 * - BUG FIX: replaced `useState(() => { loadResidents(); }, [])` with a
 *   real `useEffect` — prior code was a useState with a lazy initializer
 *   calling a side effect during render, which happened to work but
 *   bypassed React's effect lifecycle and ignored the deps arg.
 * - STATUS_CONFIG now uses COLORS.accentLight / dangerLight tokens (was
 *   hardcoded '#E8F8F0' / '#FDEDEC') and carries a new `alpha` field
 *   (accentAlpha, dangerAlpha, etc.) for border colors.
 * - Replaced broken `cfg.color + '44'` pattern on scheduled-med card
 *   border with `cfg.alpha` — the string concat was producing invalid
 *   CSS like "var(--color-accent)44".
 * - Replaced `background: '#fff'` throughout with COLORS.surface so
 *   cards, modals, form inputs, and toggle buttons theme correctly in
 *   dark mode. Left `color: '#fff'` (text on colored buttons) and the
 *   single intentional inverse "+ Add Medication" button untouched.
 * - Replaced remaining hardcoded hex: '#FDEDEC' → COLORS.dangerLight
 *   (6 instances), '#856404' → COLORS.warning (vitals-check banner),
 *   'rgba(231,76,60,0.3)' → COLORS.dangerAlpha (pending badge).
 * - A11y: FormField / FormSelect now use useId() to pair their label
 *   with their input via htmlFor/id (fixes label-has-associated-control).
 *   Both controls also now carry `color: COLORS.text` so they render
 *   correctly against their themed surface background.
 *
 * Changes (2026-06-04) — Actual administration time:
 * - AdminModal and PRNModal now capture the time the dose was actually
 *   given via a <input type="datetime-local"> prefilled to now
 *   (formatForDatetimeLocal) and editable. On submit the local value is
 *   converted to the server frame with toServerDt() and posted as
 *   `administered_at`; the backend stores it (defaulting to NOW() when
 *   omitted). In AdminModal the field shows only for the 'administered'
 *   status — refused/held doses have no administration time.
 *
 * Changes (2026-06-10) — MAR amendment UI (correction path):
 * - AmendModal corrects a posted scheduled OR PRN administration. It
 *   prefills the record's current values, requires an amendment reason,
 *   and posts to mar.php?action=amend. The backend diffs the payload and
 *   stores a before/after image in mar_amendments (original preserved).
 *   Editable: status (scheduled), time, dose+unit, route, witness,
 *   resident response / refusal-hold reason (scheduled), indication +
 *   pain-before (PRN), notes.
 * - "Amend" buttons on documented scheduled rows and PRN-given rows
 *   (licensed staff only). An "✎ Amended" badge shows on records that
 *   have a correction trail and opens AmendHistoryModal (before → after).
 * - The per-resident amendment trail loads alongside the MAR
 *   (action=amendments&patient_id=…) and refreshes after each correction.
 * - Controlled-substance co-sign (Q3): for controlled meds a co-sign
 *   witness field is shown and required (client + server) only when the
 *   correction changes the dose or the administration witness — narcotic
 *   count integrity. Time/route/note/status fixes need only reason+author.
 * - NOTE: entered-in-error (void) is intentionally NOT exposed here; the
 *   backend gates it off (409) pending the void build. Status corrections
 *   cover the "should not have given it" case for now.
 *
 * Changes (2026-06-10b) — Witness picker:
 * - Witness / co-sign inputs in AdminModal, PRNModal, and AmendModal are
 *   now a WitnessSelect dropdown (name + role) sourced once on mount from
 *   mar.php?action=witnesses (active, licensed, same tenant, excluding
 *   self). Replaces the free-text membership-ID field, which was prone to
 *   typos and could FK-500 on a bad ID. An out-of-list current value
 *   (e.g. an existing record's witness) is preserved as a fallback option.
 */
import { useState, useCallback, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import client from '../../api/client';
import { useAuth } from '../auth/useAuth';

import { formatTime, formatForDatetimeLocal, toServerDt } from '../../utils/formatters';
const MED_BASE = '/adl/medications.php';
const MAR_BASE = '/adl/mar.php';

const LICENSED_ROLES = ['Nurse', 'Care Supervisor', 'Care Admin', 'Physician'];

const DOSE_UNITS = ['mg','mcg','g','ml','units','mEq','drops','puffs','patch','application','other'];

function localDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// Strip leading "Dr.", "Dr", or "Doctor" so the display can prepend "Dr. "
// without risk of double-prefix. Case-insensitive, trims whitespace.
function stripDrPrefix(name) {
  if (!name) return name;
  return String(name).replace(/^\s*(dr\.?|doctor)\s+/i, '').trim();
}

const ROUTE_LABELS = {
  oral: 'Oral', sublingual: 'Sublingual', topical: 'Topical',
  injection_im: 'IM Injection', injection_sq: 'SQ Injection', injection_iv: 'IV',
  inhalation: 'Inhaled', patch: 'Patch', suppository: 'Suppository',
  ophthalmic: 'Eye Drops', otic: 'Ear Drops', nasal: 'Nasal', other: 'Other',
};

const FREQ_LABELS = {
  once_daily: 'Daily', twice_daily: 'Twice Daily', three_times_daily: 'Three Times Daily',
  four_times_daily: 'Four Times Daily', every_4h: 'Every 4h', every_6h: 'Every 6h',
  every_8h: 'Every 8h', every_12h: 'Every 12h', weekly: 'Weekly',
  biweekly: 'Biweekly', monthly: 'Monthly', prn: 'PRN (As Needed)', other: 'Other',
};

const STATUS_CONFIG = {
  administered:  { label: 'Given',         color: COLORS.accent,    bg: COLORS.accentLight,  alpha: COLORS.accentAlpha  },
  refused:       { label: 'Refused',       color: COLORS.danger,    bg: COLORS.dangerLight,  alpha: COLORS.dangerAlpha  },
  held:          { label: 'Held',          color: COLORS.warning,   bg: COLORS.warningLight, alpha: COLORS.warningAlpha },
  not_available: { label: 'Not Available', color: COLORS.textMuted, bg: COLORS.bg,           alpha: COLORS.border       },
  see_notes:     { label: 'See Notes',     color: COLORS.primary,   bg: COLORS.primaryLight, alpha: COLORS.primaryAlpha },
  pending:       { label: 'Pending',       color: COLORS.textMuted, bg: COLORS.bg,           alpha: COLORS.border       },
};

// ── Amendment trail rendering ────────────────────────────────
const AMEND_FIELD_LABELS = {
  status: 'Status', administered_at: 'Time', dose_given: 'Dose', dose_unit: 'Unit',
  route_used: 'Route', witness_id: 'Witness ID', resident_response: 'Response',
  refusal_reason: 'Refusal reason', hold_reason: 'Hold reason',
  scheduled_time: 'Scheduled time', indication_given: 'Indication',
  pain_score_before: 'Pain before', notes: 'Notes',
};

function amendVal(key, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (key === 'administered_at') return formatTime(v);
  if (key === 'status') return STATUS_CONFIG[v]?.label || v;
  if (key === 'route_used') return ROUTE_LABELS[v] || v;
  return String(v);
}

const amendBtnStyle = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid ' + COLORS.primary,
  background: COLORS.surface, color: COLORS.primary, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};
const amendedBadgeStyle = {
  padding: '3px 8px', borderRadius: 6, border: '1px solid ' + COLORS.warning,
  background: COLORS.warningLight, color: COLORS.warning, fontSize: 11, fontWeight: 700, cursor: 'pointer',
};

export default function MARPage() {
  const navigate = useNavigate();
  const { memberships } = useAuth();
  const role       = memberships?.[0]?.role ?? '';
  const isLicensed = LICENSED_ROLES.includes(role);

  const [view, setView]                         = useState('list');
  const [residents, setResidents]               = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [selectedResident, setSelectedResident] = useState(null);

  const [schedule, setSchedule]     = useState([]);
  const [prnMeds, setPrnMeds]       = useState([]);
  const [prnRecords, setPrnRecords] = useState([]);
  const [amendments, setAmendments] = useState([]);
  const [marLoading, setMarLoading] = useState(false);

  const [medications, setMedications] = useState([]);
  const [medLoading, setMedLoading]   = useState(false);

  const [adminModal, setAdminModal]   = useState(null);
  const [prnModal, setPrnModal]       = useState(null);
  const [amendModal, setAmendModal]   = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [addMedModal, setAddMedModal] = useState(false);
  const [editMedModal, setEditMedModal] = useState(null);
  const [discModal, setDiscModal]     = useState(null);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [witnesses, setWitnesses]     = useState([]);

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const loadResidents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`${MED_BASE}?action=residents`);
      if (res.data.success) setResidents(res.data.residents || []);
    } catch { setError('Failed to load residents'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadResidents(); }, [loadResidents]);

  // Eligible witnesses (active, licensed, same tenant, excluding self).
  // Non-licensed viewers can't list them; the catch leaves the list empty.
  const loadWitnesses = useCallback(async () => {
    try {
      const res = await client.get(`${MAR_BASE}?action=witnesses`);
      if (res.data.success) setWitnesses(res.data.witnesses || []);
    } catch { /* viewer lacks witness-list access; leave empty */ }
  }, []);

  useEffect(() => { loadWitnesses(); }, [loadWitnesses]);

  const loadMAR = useCallback(async (resident) => {
    setMarLoading(true);
    try {
      const res = await client.get(`${MAR_BASE}?action=today&patient_id=${resident.patient_id}`);
      if (res.data.success) {
        setSchedule(res.data.schedule || []);
        setPrnMeds(res.data.prn_meds || []);
        setPrnRecords(res.data.prn_records || []);
      }
      const amRes = await client.get(`${MAR_BASE}?action=amendments&patient_id=${resident.patient_id}`);
      if (amRes.data.success) setAmendments(amRes.data.amendments || []);
    } catch { setError('Failed to load MAR'); }
    finally { setMarLoading(false); }
  }, []);

  const loadMedications = useCallback(async (resident) => {
    setMedLoading(true);
    try {
      const res = await client.get(`${MED_BASE}?action=list&patient_id=${resident.patient_id}&include_inactive=1`);
      if (res.data.success) setMedications(res.data.medications || []);
    } catch { setError('Failed to load medications'); }
    finally { setMedLoading(false); }
  }, []);

  function selectResident(resident) {
    setSelectedResident(resident);
    setView('mar');
    loadMAR(resident);
  }

  function switchToMedications() {
    setView('medications');
    loadMedications(selectedResident);
  }

  const handleAdminister = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${MAR_BASE}?action=administer`, {
        medication_id:  adminModal.slot.medication.medication_id,
        patient_id:     selectedResident.patient_id,
        scheduled_time: adminModal.slot.scheduled_time,
        ...formData,
      });
      if (res.data.success) { setAdminModal(null); loadMAR(selectedResident); loadResidents(); }
      else setError(res.data.error || 'Failed to document');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to document'); }
    finally { setSaving(false); }
  };

  const handlePRN = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${MAR_BASE}?action=prn`, {
        medication_id: prnModal.med.medication_id,
        patient_id:    selectedResident.patient_id,
        ...formData,
      });
      if (res.data.success) { setPrnModal(null); loadMAR(selectedResident); }
      else setError(res.data.error || 'Failed to document PRN');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to document PRN'); }
    finally { setSaving(false); }
  };

  const handleAmend = async (payload) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${MAR_BASE}?action=amend`, payload);
      if (res.data.success) { setAmendModal(null); loadMAR(selectedResident); }
      else setError(res.data.error || 'Failed to amend record');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to amend record'); }
    finally { setSaving(false); }
  };

  const handleAddMed = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${MED_BASE}?action=add`, { patient_id: selectedResident.patient_id, ...formData });
      if (res.data.success) { setAddMedModal(false); loadMedications(selectedResident); loadResidents(); }
      else setError(res.data.error || 'Failed to add medication');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to add medication'); }
    finally { setSaving(false); }
  };

  const handleEditMed = async (formData) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${MED_BASE}?action=update`, {
        medication_id: editMedModal.medication_id,
        ...formData,
      });
      if (res.data.success) { setEditMedModal(null); loadMedications(selectedResident); loadMAR(selectedResident); loadResidents(); }
      else setError(res.data.error || 'Failed to update medication');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to update medication'); }
    finally { setSaving(false); }
  };

  const handleDiscontinue = async (reason) => {
    setSaving(true); setError(null);
    try {
      const res = await client.post(`${MED_BASE}?action=discontinue`, { medication_id: discModal.medication_id, reason });
      if (res.data.success) { setDiscModal(null); loadMedications(selectedResident); loadResidents(); }
      else setError(res.data.error || 'Failed to discontinue');
    } catch (err) { setError(err?.response?.data?.error || 'Failed to discontinue'); }
    finally { setSaving(false); }
  };

  // ── RENDER: Resident list ───────────────────────────────
  if (view === 'list') {
    return (
      <div style={{ paddingBottom: 80 }}>
        <div style={{ padding: '12px 16px', background: COLORS.surface, borderBottom: '1px solid ' + COLORS.border, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/dashboard/more')} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.primary, padding: '4px 8px' }}>← Back</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>Medication Administration</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>{today}</div>
          </div>
        </div>
        <div style={{ padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading residents...</div>
          ) : residents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💊</div>
              <div>No residents found.</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 12 }}>Select a resident to view or document their MAR.</div>
              {residents.map(r => (
                <div
                  key={r.patient_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectResident(r)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && selectResident(r)}
                  style={{ background: COLORS.surface, borderRadius: 10, padding: '14px 16px', marginBottom: 8, border: '1px solid ' + COLORS.border, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, background: COLORS.primaryLight, color: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700 }}>
                    {r.first_name[0]}{r.last_name[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{r.first_name} {r.last_name}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>Rm {r.room_number}{r.bed_label ? ` · ${r.bed_label}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>{r.total_medications} med{r.total_medications !== 1 ? 's' : ''}</div>
                    {r.controlled_count > 0 && <div style={{ fontSize: 11, color: COLORS.danger, fontWeight: 600 }}>⚠ {r.controlled_count} controlled</div>}
                    {r.prn_count > 0 && <div style={{ fontSize: 11, color: COLORS.textMuted }}>{r.prn_count} PRN</div>}
                  </div>
                  <span style={{ color: COLORS.textMuted }}>›</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── RENDER: MAR detail ──────────────────────────────────
  if (view === 'mar') {
    const pendingCount = schedule.filter(s => s.status === 'pending').length;
    return (
      <div style={{ paddingBottom: 80 }}>
        <div style={{ padding: '12px 16px', background: COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>←</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{selectedResident.first_name} {selectedResident.last_name}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>MAR · {today}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {pendingCount > 0 && <span style={{ fontSize: 11, fontWeight: 700, background: COLORS.dangerAlpha, color: '#fff', padding: '3px 8px', borderRadius: 20 }}>{pendingCount} pending</span>}
            {isLicensed && (
              <button onClick={switchToMedications} style={{ fontSize: 12, fontWeight: 600, background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>
                Manage Meds
              </button>
            )}
          </div>
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div style={{ padding: '12px 16px' }}>
          {marLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading MAR...</div>
          ) : (
            <>
              {schedule.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <SectionHeader title="Scheduled Medications" icon="📋" />
                  {schedule.map((slot, i) => {
                    const cfg = STATUS_CONFIG[slot.status] || STATUS_CONFIG.pending;
                    const med = slot.medication;
                    const recAmendments = slot.mar_record
                      ? amendments.filter(a => a.mar_record_id === slot.mar_record.mar_record_id)
                      : [];
                    return (
                      <div key={i} style={{ background: COLORS.surface, borderRadius: 10, border: '1px solid ' + (slot.status === 'pending' ? COLORS.border : cfg.alpha), marginBottom: 8, overflow: 'hidden' }}>
                        <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{med.drug_name}</span>
                              {!!med.is_controlled && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerLight, padding: '1px 5px', borderRadius: 4 }}>C-{med.controlled_schedule}</span>}
                            </div>
                            <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                              {parseFloat(med.dose)} {med.dose_unit} · {ROUTE_LABELS[med.route] || med.route}
                              {slot.scheduled_time && ` · ${formatTime12(slot.scheduled_time)}`}
                            </div>
                            {med.special_instructions && <div style={{ fontSize: 11, color: COLORS.warning, marginTop: 2 }}>⚠ {med.special_instructions}</div>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '3px 8px', borderRadius: 6 }}>{cfg.label}</span>
                            {isLicensed && slot.status === 'pending' && (
                              <button onClick={() => setAdminModal({ slot })} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                Document
                              </button>
                            )}
                          </div>
                        </div>
                        {slot.mar_record && (
                          <div style={{ padding: '6px 14px 10px', borderTop: '1px solid ' + COLORS.bg }}>
                            <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                              {slot.mar_record.administered_by_name && `By ${slot.mar_record.administered_by_name}`}
                              {slot.mar_record.administered_at && ` · ${formatTime(slot.mar_record.administered_at)}`}
                              {slot.mar_record.refusal_reason && ` · Reason: ${slot.mar_record.refusal_reason}`}
                              {slot.mar_record.resident_response && ` · Response: ${slot.mar_record.resident_response}`}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                              {recAmendments.length > 0 && (
                                <button onClick={() => setHistoryModal({ amendments: recAmendments })} style={amendedBadgeStyle}>
                                  ✎ Amended{recAmendments.length > 1 ? ` ×${recAmendments.length}` : ''}
                                </button>
                              )}
                              {isLicensed && (
                                <button onClick={() => setAmendModal({ recordType: 'scheduled', record: slot.mar_record, medication: med })} style={amendBtnStyle}>
                                  Amend
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {prnMeds.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <SectionHeader title="PRN (As Needed)" icon="🔔" />
                  {prnMeds.map(med => {
                    const todayCount = prnRecords.filter(r => r.medication_id === med.medication_id).length;
                    return (
                      <div key={med.medication_id} style={{ background: COLORS.surface, borderRadius: 10, border: '1px solid ' + COLORS.border, marginBottom: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{med.drug_name}</span>
                            {!!med.is_controlled && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerLight, padding: '1px 5px', borderRadius: 4 }}>C-{med.controlled_schedule}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: COLORS.textMuted }}>{parseFloat(med.dose)} {med.dose_unit} · {ROUTE_LABELS[med.route] || med.route}</div>
                          {med.indication && <div style={{ fontSize: 11, color: COLORS.primary, marginTop: 2 }}>For: {med.indication}</div>}
                          {todayCount > 0 && <div style={{ fontSize: 11, color: COLORS.warning, marginTop: 2 }}>Given {todayCount}x today</div>}
                        </div>
                        {isLicensed && (
                          <button onClick={() => setPrnModal({ med })} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid ' + COLORS.primary, background: COLORS.surface, color: COLORS.primary, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            Give PRN
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {prnRecords.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <SectionHeader title="PRN Given Today" icon="📝" />
                  {prnRecords.map(r => {
                    const recAmendments = amendments.filter(a => a.prn_record_id === r.prn_record_id);
                    return (
                      <div key={r.prn_record_id} style={{ background: COLORS.surface, borderRadius: 8, border: '1px solid ' + COLORS.border, marginBottom: 6, padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{r.drug_name} {parseFloat(r.dose_given)} {r.dose_unit}</span>
                          <span style={{ fontSize: 11, color: COLORS.textMuted }}>{formatTime(r.administered_at)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>For: {r.indication_given}</div>
                        {r.administered_by_name && <div style={{ fontSize: 11, color: COLORS.textMuted }}>By {r.administered_by_name}</div>}
                        {r.effectiveness_outcome && (
                          <div style={{ fontSize: 11, marginTop: 4, color: r.effectiveness_outcome === 'effective' ? COLORS.accent : COLORS.warning }}>
                            Outcome: {r.effectiveness_outcome}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          {recAmendments.length > 0 && (
                            <button onClick={() => setHistoryModal({ amendments: recAmendments })} style={amendedBadgeStyle}>
                              ✎ Amended{recAmendments.length > 1 ? ` ×${recAmendments.length}` : ''}
                            </button>
                          )}
                          {isLicensed && (
                            <button onClick={() => setAmendModal({ recordType: 'prn', record: r })} style={amendBtnStyle}>
                              Amend
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {schedule.length === 0 && prnMeds.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>💊</div>
                  <div style={{ fontWeight: 600 }}>No medications on file</div>
                  {isLicensed && (
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                      Use &ldquo;Manage Meds&rdquo; to add medications for this resident.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {adminModal && <AdminModal slot={adminModal.slot} witnesses={witnesses} onConfirm={handleAdminister} onCancel={() => setAdminModal(null)} saving={saving} error={error} />}
        {prnModal && <PRNModal med={prnModal.med} witnesses={witnesses} onConfirm={handlePRN} onCancel={() => setPrnModal(null)} saving={saving} error={error} />}
        {amendModal && <AmendModal recordType={amendModal.recordType} record={amendModal.record} medication={amendModal.medication} witnesses={witnesses} onConfirm={handleAmend} onCancel={() => setAmendModal(null)} saving={saving} error={error} />}
        {historyModal && <AmendHistoryModal amendments={historyModal.amendments} onClose={() => setHistoryModal(null)} />}
      </div>
    );
  }

  // ── RENDER: Medication management ──────────────────────
  if (view === 'medications') {
    const activeMeds   = medications.filter(m => m.is_active);
    const inactiveMeds = medications.filter(m => !m.is_active);
    return (
      <div style={{ paddingBottom: 80 }}>
        <div style={{ padding: '12px 16px', background: COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => setView('mar')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', padding: '4px 8px' }}>←</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{selectedResident.first_name} {selectedResident.last_name}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Medication Orders</div>
          </div>
          {isLicensed && (
            <button onClick={() => setAddMedModal(true)} style={{ fontSize: 13, fontWeight: 700, background: '#fff', color: COLORS.primary, border: 'none', padding: '6px 14px', borderRadius: 8, cursor: 'pointer' }}>
              + Add Medication
            </button>
          )}
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div style={{ padding: '12px 16px' }}>
          {medLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>Loading medications...</div>
          ) : (
            <>
              {activeMeds.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>💊</div>
                  <div>No active medications.</div>
                </div>
              )}
              {activeMeds.map(med => (
                <MedicationCard key={med.medication_id} med={med} isLicensed={isLicensed}
                  onEdit={() => setEditMedModal(med)}
                  onDiscontinue={() => setDiscModal(med)} />
              ))}
              {inactiveMeds.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', margin: '16px 0 8px', letterSpacing: 0.5 }}>Discontinued</div>
                  {inactiveMeds.map(med => (
                    <MedicationCard key={med.medication_id} med={med} isLicensed={false} discontinued />
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {addMedModal && <AddMedicationModal onConfirm={handleAddMed} onCancel={() => setAddMedModal(false)} saving={saving} error={error} />}
        {editMedModal && <EditMedicationModal med={editMedModal} onConfirm={handleEditMed} onCancel={() => setEditMedModal(null)} saving={saving} error={error} />}
        {discModal && <DiscontinueModal med={discModal} onConfirm={handleDiscontinue} onCancel={() => setDiscModal(null)} saving={saving} />}
      </div>
    );
  }

  return null;
}

// ── Sub-components ────────────────────────────────────────────

function SectionHeader({ title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
    </div>
  );
}

function MedicationCard({ med, isLicensed, discontinued, onEdit, onDiscontinue }) {
  return (
    <div style={{ background: discontinued ? COLORS.bg : COLORS.surface, borderRadius: 10, border: '1px solid ' + COLORS.border, marginBottom: 8, padding: '12px 14px', opacity: discontinued ? 0.7 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{med.drug_name}</span>
            {med.brand_name && med.brand_name !== 0 && <span style={{ fontSize: 11, color: COLORS.textMuted }}>({med.brand_name})</span>}
            {!!med.is_controlled && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerLight, padding: '1px 5px', borderRadius: 4 }}>C-{med.controlled_schedule}</span>}
            {med.frequency === 'prn' && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.primary, background: COLORS.primaryLight, padding: '1px 5px', borderRadius: 4 }}>PRN</span>}
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>
            {parseFloat(med.dose)} {med.dose_unit} · {ROUTE_LABELS[med.route] || med.route} · {FREQ_LABELS[med.frequency] || med.frequency}
          </div>
          {med.scheduled_times?.length > 0 && (
            <div style={{ fontSize: 11, color: COLORS.primary, marginTop: 2 }}>
              Times: {med.scheduled_times.map(formatTime12).join(', ')}
            </div>
          )}
          {med.indication && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>For: {med.indication}</div>}
          {med.prescribing_physician && <div style={{ fontSize: 11, color: COLORS.textMuted }}>Dr. {med.prescribing_physician}</div>}
          {med.special_instructions && <div style={{ fontSize: 11, color: COLORS.warning, marginTop: 4 }}>⚠ {med.special_instructions}</div>}
          {discontinued && med.discontinue_reason && (
            <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 4 }}>Discontinued: {med.discontinue_reason}</div>
          )}
        </div>
        {isLicensed && !discontinued && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            <button onClick={onEdit} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid ' + COLORS.primary, background: COLORS.surface, color: COLORS.primary, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Edit</button>
            <button onClick={onDiscontinue} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid ' + COLORS.danger, background: COLORS.surface, color: COLORS.danger, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>D/C</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 * PATCH for MARPage.jsx — deferred co-sign (no-witness) affordance
 * 2026-06-17
 *
 * HOW TO APPLY (3 edits, nothing else in the file changes):
 *   1. Add the changelog block below to the top doc-comment, after the
 *      "Changes (2026-06-10b) — Witness picker" block.
 *   2. Replace the entire `function AdminModal(...) { ... }` with the
 *      version below.
 *   3. Replace the entire `function PRNModal(...) { ... }` with the
 *      version below.
 *
 * No imports change. No API change — the backend already records a
 * controlled dose with witness_id omitted as a deferred co-sign
 * (cosign_required=1, cosigned_at NULL), cleared later via action=cosign.
 * ============================================================ */
function AdminModal({ slot, witnesses, onConfirm, onCancel, saving, error }) {
  const med = slot.medication;
  const [status, setStatus]       = useState('administered');
  const [administeredAt, setAdministeredAt] = useState(formatForDatetimeLocal());
  const [reason, setReason]       = useState('');
  const [response, setResponse]   = useState('');
  const [notes, setNotes]         = useState('');
  const [witnessId, setWitnessId] = useState('');
  const [deferCosign, setDeferCosign] = useState(false);

  const needsReason   = status === 'refused' || status === 'held';
  // Co-sign only applies to an actual controlled administration.
  const coSignApplies = !!med.is_controlled && status === 'administered';
  const noWitnesses   = (witnesses || []).length === 0;

  return (
    <Modal title={`Document: ${med.drug_name}`} onClose={onCancel}>
      <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 14 }}>
        {parseFloat(med.dose)} {med.dose_unit} · {ROUTE_LABELS[med.route] || med.route}
        {slot.scheduled_time && ` · Scheduled ${formatTime12(slot.scheduled_time)}`}
      </div>
      {coSignApplies && (
        deferCosign ? (
          <div style={{ background: COLORS.warningLight, border: '1px solid ' + COLORS.warning, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.warning, fontWeight: 600 }}>
            ⚠ Co-sign deferred — the dose is recorded now and the co-sign stays pending. An eligible witness must complete it by the end of the next shift.
          </div>
        ) : (
          <div style={{ background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>
            ⚠ Controlled Substance (Schedule {med.controlled_schedule}) — a second-licensed witness is required. If none is available, use the defer option below.
          </div>
        )
      )}
      {!!med.requires_vitals_check && (
        <div style={{ background: COLORS.warningLight, border: '1px solid ' + COLORS.warning, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.warning, fontWeight: 600 }}>
          ⚠ Vitals check required before administration
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Administration Status *</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'pending').map(([k, v]) => (
            <button key={k} onClick={() => setStatus(k)} style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: status === k ? v.color : COLORS.surface,
              color: status === k ? '#fff' : COLORS.textMuted,
              border: '1px solid ' + (status === k ? v.color : COLORS.border),
            }}>{v.label}</button>
          ))}
        </div>
      </div>
      {status === 'administered' && (
        <FormField label="Time Administered" value={administeredAt} onChange={setAdministeredAt} type="datetime-local" />
      )}
      {coSignApplies && !deferCosign && (
        <WitnessSelect label="Witness *" value={witnessId} onChange={setWitnessId} witnesses={witnesses} />
      )}
      {coSignApplies && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: COLORS.textMuted, margin: '2px 0 12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={deferCosign} onChange={e => { setDeferCosign(e.target.checked); if (e.target.checked) setWitnessId(''); }} style={{ marginTop: 2 }} />
          <span>
            No eligible witness available — document now and defer the co-sign.
            {noWitnesses && !deferCosign && <span style={{ color: COLORS.warning }}> (No witnesses are currently checked in.)</span>}
          </span>
        </label>
      )}
      {needsReason && <FormField label={status === 'refused' ? 'Refusal Reason *' : 'Hold Reason *'} value={reason} onChange={setReason} placeholder="Enter reason..." />}
      <FormField label="Resident Response (optional)" value={response} onChange={setResponse} placeholder="e.g. Tolerated well, complained of nausea..." />
      <FormField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Additional notes..." />
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onConfirm({ status, administered_at: status === 'administered' ? toServerDt(administeredAt) : undefined, refusal_reason: reason, hold_reason: reason, resident_response: response, notes, witness_id: (!deferCosign && witnessId) ? parseInt(witnessId) : undefined })}
          disabled={saving || (needsReason && !reason) || (coSignApplies && !witnessId && !deferCosign)}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Confirm'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// 3. REPLACEMENT: function PRNModal
// ============================================================
function PRNModal({ med, witnesses, onConfirm, onCancel, saving, error }) {
  const [indication, setIndication] = useState('');
  const [administeredAt, setAdministeredAt] = useState(formatForDatetimeLocal());
  const [painBefore, setPainBefore] = useState('');
  const [notes, setNotes]           = useState('');
  const [witnessId, setWitnessId]   = useState('');
  const [deferCosign, setDeferCosign] = useState(false);

  // A PRN dose is always an actual administration, so co-sign applies
  // whenever the med is controlled.
  const coSignApplies = !!med.is_controlled;
  const noWitnesses   = (witnesses || []).length === 0;

  return (
    <Modal title={`Give PRN: ${med.drug_name}`} onClose={onCancel}>
      <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 14 }}>
        {parseFloat(med.dose)} {med.dose_unit} · {ROUTE_LABELS[med.route] || med.route}
      </div>
      {coSignApplies && (
        deferCosign ? (
          <div style={{ background: COLORS.warningLight, border: '1px solid ' + COLORS.warning, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.warning, fontWeight: 600 }}>
            ⚠ Co-sign deferred — the dose is recorded now and the co-sign stays pending. An eligible witness must complete it by the end of the next shift.
          </div>
        ) : (
          <div style={{ background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>
            ⚠ Controlled Substance — a second-licensed witness is required. If none is available, use the defer option below.
          </div>
        )
      )}
      <FormField label="Indication (reason for giving) *" value={indication} onChange={setIndication} placeholder="e.g. Complaint of pain 7/10, agitation, shortness of breath..." />
      <FormField label="Time Given" value={administeredAt} onChange={setAdministeredAt} type="datetime-local" />
      <FormField label="Pain Score Before (0–10)" value={painBefore} onChange={setPainBefore} type="number" placeholder="0–10 or leave blank" />
      {coSignApplies && !deferCosign && (
        <WitnessSelect label="Witness *" value={witnessId} onChange={setWitnessId} witnesses={witnesses} />
      )}
      {coSignApplies && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: COLORS.textMuted, margin: '2px 0 12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={deferCosign} onChange={e => { setDeferCosign(e.target.checked); if (e.target.checked) setWitnessId(''); }} style={{ marginTop: 2 }} />
          <span>
            No eligible witness available — document now and defer the co-sign.
            {noWitnesses && !deferCosign && <span style={{ color: COLORS.warning }}> (No witnesses are currently checked in.)</span>}
          </span>
        </label>
      )}
      <FormField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Additional notes..." />
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onConfirm({ indication_given: indication, administered_at: toServerDt(administeredAt), pain_score_before: painBefore || undefined, notes, witness_id: (!deferCosign && witnessId) ? parseInt(witnessId) : undefined })}
          disabled={saving || !indication || (coSignApplies && !witnessId && !deferCosign)}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Document PRN'}
        </button>
      </div>
    </Modal>
  );
}

// ── AMEND: correct a posted scheduled or PRN administration ──
// Prefills the record's current values; the backend (mar.php?action=
// amend) diffs the payload, updates the row in place, and stores a
// before/after image in mar_amendments. A reason is always required.
// Controlled-substance co-sign (Q3) is required only when the dose or
// the administration witness changes — mirrored server-side.
function AmendModal({ recordType, record, medication, witnesses, onConfirm, onCancel, saving, error }) {
  const isPrn = recordType === 'prn';
  const isControlled = isPrn ? !!record.is_controlled : !!medication?.is_controlled;
  const drugName = isPrn ? record.drug_name : medication?.drug_name;

  const [status, setStatus] = useState(record.status || 'administered');
  const [administeredAt, setAdministeredAt] = useState(
    record.administered_at ? formatForDatetimeLocal(record.administered_at) : formatForDatetimeLocal()
  );
  const [doseGiven, setDoseGiven] = useState(record.dose_given != null ? String(parseFloat(record.dose_given)) : '');
  const [doseUnit, setDoseUnit]   = useState(record.dose_unit || 'mg');
  const [routeUsed, setRouteUsed] = useState(record.route_used || 'oral');
  const [witnessId, setWitnessId] = useState(record.witness_id != null ? String(record.witness_id) : '');
  const [response, setResponse]   = useState(record.resident_response || '');
  const [reasonText, setReasonText] = useState(record.refusal_reason || record.hold_reason || '');
  const [indication, setIndication] = useState(record.indication_given || '');
  const [painBefore, setPainBefore] = useState(record.pain_score_before != null ? String(record.pain_score_before) : '');
  const [notes, setNotes] = useState(record.notes || '');
  const [amendReason, setAmendReason] = useState('');
  const [amendWitnessId, setAmendWitnessId] = useState('');

  const showTime = isPrn || status === 'administered';
  const needsReason = !isPrn && (status === 'refused' || status === 'held');

  const doseChanged = (doseGiven === '' ? null : parseFloat(doseGiven))
    !== (record.dose_given == null ? null : parseFloat(record.dose_given));
  const witnessChanged = (witnessId === '' ? null : parseInt(witnessId, 10))
    !== (record.witness_id == null ? null : parseInt(record.witness_id, 10));
  const coSignRequired = isControlled && (doseChanged || witnessChanged);

  const submit = () => {
    const base = {
      record_type: recordType,
      reason: amendReason.trim(),
      dose_given: doseGiven === '' ? null : parseFloat(doseGiven),
      dose_unit: doseUnit,
      route_used: routeUsed,
      witness_id: witnessId === '' ? null : parseInt(witnessId, 10),
      notes: notes || null,
      amend_witness_id: amendWitnessId ? parseInt(amendWitnessId, 10) : undefined,
    };
    if (isPrn) {
      onConfirm({
        ...base,
        prn_record_id: record.prn_record_id,
        administered_at: toServerDt(administeredAt),
        indication_given: indication,
        pain_score_before: painBefore === '' ? null : parseInt(painBefore, 10),
      });
    } else {
      onConfirm({
        ...base,
        mar_record_id: record.mar_record_id,
        status,
        administered_at: status === 'administered' ? toServerDt(administeredAt) : null,
        resident_response: response || null,
        refusal_reason: status === 'refused' ? reasonText : null,
        hold_reason: status === 'held' ? reasonText : null,
      });
    }
  };

  const submitDisabled = saving
    || !amendReason.trim()
    || (needsReason && !reasonText)
    || (coSignRequired && !amendWitnessId)
    || (isPrn && !indication.trim());

  return (
    <Modal title={`Amend: ${drugName}`} onClose={onCancel} wide>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 14 }}>
        Correcting a posted {isPrn ? 'PRN' : 'scheduled'} administration. The original values are preserved in the amendment trail.
      </div>
      {isControlled && (
        <div style={{ background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>
          ⚠ Controlled substance — a co-signing witness is required when the dose or administration witness is changed.
        </div>
      )}

      {!isPrn && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Status *</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'pending').map(([k, v]) => (
              <button key={k} onClick={() => setStatus(k)} style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: status === k ? v.color : COLORS.surface,
                color: status === k ? '#fff' : COLORS.textMuted,
                border: '1px solid ' + (status === k ? v.color : COLORS.border),
              }}>{v.label}</button>
            ))}
          </div>
        </div>
      )}

      {showTime && (
        <FormField label="Time Administered" value={administeredAt} onChange={setAdministeredAt} type="datetime-local" />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormField label="Dose Given" value={doseGiven} onChange={setDoseGiven} type="number" placeholder="e.g. 25" />
        <FormSelect label="Unit" value={doseUnit} onChange={setDoseUnit} options={DOSE_UNITS} />
      </div>
      <FormSelect label="Route" value={routeUsed} onChange={setRouteUsed} options={Object.keys(ROUTE_LABELS)} labels={ROUTE_LABELS} />

      {isPrn && (
        <>
          <FormField label="Indication *" value={indication} onChange={setIndication} placeholder="Reason the dose was given" />
          <FormField label="Pain Score Before (0–10)" value={painBefore} onChange={setPainBefore} type="number" placeholder="0–10 or blank" />
        </>
      )}

      {needsReason && (
        <FormField label={status === 'refused' ? 'Refusal Reason *' : 'Hold Reason *'} value={reasonText} onChange={setReasonText} placeholder="Enter reason..." />
      )}
      {!isPrn && (
        <FormField label="Resident Response (optional)" value={response} onChange={setResponse} placeholder="e.g. Tolerated well..." />
      )}

      {isControlled && (
        <WitnessSelect label="Administration Witness" value={witnessId} onChange={setWitnessId} witnesses={witnesses} placeholder="No witness recorded" />
      )}

      <FormField label="Notes (optional)" value={notes} onChange={setNotes} placeholder="Additional notes..." />

      <div style={{ borderTop: '1px solid ' + COLORS.border, marginTop: 4, paddingTop: 12 }}>
        <FormField label="Reason for Amendment *" value={amendReason} onChange={setAmendReason} placeholder="Why is this record being corrected?" />
        {isControlled && (
          <WitnessSelect
            label={coSignRequired ? 'Co-Sign Witness *' : 'Co-Sign Witness (required for dose/witness changes)'}
            value={amendWitnessId} onChange={setAmendWitnessId} witnesses={witnesses} placeholder="Select co-signing witness…" />
        )}
      </div>

      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={submit} disabled={submitDisabled}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: submitDisabled ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save Correction'}
        </button>
      </div>
    </Modal>
  );
}

// ── Amendment trail viewer ──────────────────────────────────
function AmendHistoryModal({ amendments, onClose }) {
  return (
    <Modal title="Amendment History" onClose={onClose} wide>
      {amendments.length === 0 ? (
        <div style={{ fontSize: 13, color: COLORS.textMuted }}>No amendments recorded.</div>
      ) : (
        amendments.map(a => {
          const before = a.before || {};
          const after  = a.after  || {};
          const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
          return (
            <div key={a.amendment_id} style={{ background: COLORS.bg, borderRadius: 8, border: '1px solid ' + COLORS.border, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
                {a.amended_by_name || 'Staff'} · {formatTime(a.amended_at)}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted, margin: '2px 0 8px' }}>Reason: {a.reason}</div>
              {a.witness_name && <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>Co-signed by {a.witness_name}</div>}
              {keys.map(k => (
                <div key={k} style={{ fontSize: 12, color: COLORS.text, marginBottom: 2 }}>
                  <span style={{ fontWeight: 600 }}>{AMEND_FIELD_LABELS[k] || k}: </span>
                  <span style={{ color: COLORS.danger, textDecoration: 'line-through' }}>{amendVal(k, before[k])}</span>
                  <span style={{ color: COLORS.textMuted }}> → </span>
                  <span style={{ color: COLORS.accent }}>{amendVal(k, after[k])}</span>
                </div>
              ))}
            </div>
          );
        })
      )}
      <div style={{ marginTop: 8 }}>
        <button onClick={onClose} style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Close</button>
      </div>
    </Modal>
  );
}

function MedForm({ initial, onConfirm, onCancel, saving, error, title }) {
  const [form, setForm] = useState({
    drug_name:             initial?.drug_name             ?? '',
    brand_name:            initial?.brand_name            ?? '',
    dose:                  initial?.dose                  ?? '',
    dose_unit:             initial?.dose_unit             ?? 'mg',
    route:                 initial?.route                 ?? 'oral',
    frequency:             initial?.frequency             ?? 'once_daily',
    scheduled_times:       Array.isArray(initial?.scheduled_times) ? initial.scheduled_times.join(', ') : '',
    indication:            initial?.indication            ?? '',
    prescribing_physician: stripDrPrefix(initial?.prescribing_physician) ?? '',
    start_date:            initial?.start_date            ?? localDate(),
    end_date:            initial?.end_date && initial.end_date !== '0000-00-00' ? initial.end_date : '',
    is_controlled:         !!initial?.is_controlled,
    controlled_schedule:   initial?.controlled_schedule   ?? '',
    special_instructions:  initial?.special_instructions  ?? '',
    requires_vitals_check: !!initial?.requires_vitals_check,
  });
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = () => {
    const times = form.scheduled_times.trim()
      ? form.scheduled_times.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    onConfirm({
      ...form,
      scheduled_times: times,
      dose: parseFloat(form.dose),
      prescribing_physician: stripDrPrefix(form.prescribing_physician),
    });
  };

  return (
    <Modal title={title} onClose={onCancel} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1 / -1' }}><FormField label="Drug Name *" value={form.drug_name} onChange={v => u('drug_name', v)} placeholder="e.g. Metoprolol" /></div>
        <FormField label="Brand Name" value={form.brand_name} onChange={v => u('brand_name', v)} placeholder="Optional" />
        <FormField label="NDC Code" value={form.ndc_code ?? ''} onChange={v => u('ndc_code', v)} placeholder="Optional" />
        <FormField label="Dose *" value={form.dose} onChange={v => u('dose', v)} type="number" placeholder="e.g. 25" />
        <FormSelect label="Unit *" value={form.dose_unit} onChange={v => u('dose_unit', v)} options={DOSE_UNITS} />
        <FormSelect label="Route *" value={form.route} onChange={v => u('route', v)} options={Object.keys(ROUTE_LABELS)} labels={ROUTE_LABELS} />
        <FormSelect label="Frequency *" value={form.frequency} onChange={v => u('frequency', v)} options={Object.keys(FREQ_LABELS)} labels={FREQ_LABELS} />
      </div>
      {form.frequency !== 'prn' && (
        <FormField label="Scheduled Times (comma-separated, 24h format)" value={form.scheduled_times} onChange={v => u('scheduled_times', v)} placeholder="e.g. 08:00, 20:00" />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormField label="Start Date *" value={form.start_date} onChange={v => u('start_date', v)} type="date" />
        <FormField label="End Date (leave blank if ongoing)" value={form.end_date} onChange={v => u('end_date', v)} type="date" />
      </div>
      <FormField label="Indication" value={form.indication} onChange={v => u('indication', v)} placeholder="What it is prescribed for" />
      <FormField label="Prescribing Physician" value={form.prescribing_physician} onChange={v => u('prescribing_physician', v)} placeholder="Last name only — Dr. added automatically" />
      <FormField label="Special Instructions" value={form.special_instructions} onChange={v => u('special_instructions', v)} placeholder="e.g. Take with food, check BP before giving" />
      <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.is_controlled} onChange={e => u('is_controlled', e.target.checked)} />
          Controlled Substance
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.requires_vitals_check} onChange={e => u('requires_vitals_check', e.target.checked)} />
          Requires Vitals Check
        </label>
      </div>
      {form.is_controlled && (
        <FormSelect label="Schedule *" value={form.controlled_schedule} onChange={v => u('controlled_schedule', v)} options={['II','III','IV','V']} placeholder="Select schedule" />
      )}
      {error && <div style={{ color: COLORS.danger, fontSize: 12, margin: '8px 0' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={saving || !form.drug_name || !form.dose || !form.dose_unit}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : title}
        </button>
      </div>
    </Modal>
  );
}

function AddMedicationModal({ onConfirm, onCancel, saving, error }) {
  return <MedForm initial={null} onConfirm={onConfirm} onCancel={onCancel} saving={saving} error={error} title="Add Medication" />;
}

function EditMedicationModal({ med, onConfirm, onCancel, saving, error }) {
  return <MedForm initial={med} onConfirm={onConfirm} onCancel={onCancel} saving={saving} error={error} title="Save Changes" />;
}

function DiscontinueModal({ med, onConfirm, onCancel, saving }) {
  const [reason, setReason] = useState('');
  return (
    <Modal title={`Discontinue: ${med.drug_name}`} onClose={onCancel}>
      <p style={{ fontSize: 13, color: COLORS.textMuted, margin: '0 0 14px' }}>
        This will stop {med.drug_name} from appearing on the MAR. This action is recorded in the audit trail.
      </p>
      <FormField label="Reason for discontinuation *" value={reason} onChange={setReason} placeholder="e.g. Physician order, adverse reaction, therapy completed..." />
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onConfirm(reason)} disabled={saving || !reason}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.danger, color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', opacity: (saving || !reason) ? 0.6 : 1 }}>
          {saving ? 'Discontinuing…' : 'Discontinue'}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: COLORS.surface, borderRadius: 12, padding: 24, width: '100%', maxWidth: wide ? 520 : 380, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: COLORS.text }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.textMuted }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = 'text' }) {
  const id = useId();
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <input id={id} type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid ' + COLORS.border, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', background: COLORS.surface, color: COLORS.text }} />
    </div>
  );
}

function FormSelect({ label, value, onChange, options, labels, placeholder }) {
  const id = useId();
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <select id={id} value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid ' + COLORS.border, fontSize: 13, background: COLORS.surface, color: COLORS.text, boxSizing: 'border-box' }}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o} value={o}>{labels ? labels[o] : o}</option>)}
      </select>
    </div>
  );
}

// Dropdown of eligible witnesses (name + role). If the current value is a
// membership not in the eligible list (e.g. an existing record's witness who
// is now inactive, or the viewer themselves), it is kept as a fallback option
// so the select still displays it and round-trips without spuriously clearing.
function WitnessSelect({ label, value, onChange, witnesses, placeholder = 'Select witness…' }) {
  const id = useId();
  const v = (value === '' || value == null) ? '' : String(value);
  const list = [...(witnesses || [])];
  if (v && !list.some(w => String(w.membership_id) === v)) {
    list.unshift({ membership_id: v, name: `Staff #${v}`, role: 'current' });
  }
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <select id={id} value={v} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid ' + COLORS.border, fontSize: 13, background: COLORS.surface, color: COLORS.text, boxSizing: 'border-box' }}>
        <option value="">{placeholder}</option>
        {list.map(w => <option key={w.membership_id} value={w.membership_id}>{w.name} ({w.role})</option>)}
      </select>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }) {
  return (
    <div style={{ margin: '8px 16px', padding: '10px 14px', borderRadius: 8, background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, fontSize: 13, color: COLORS.danger, display: 'flex', justifyContent: 'space-between' }}>
      {message}
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer' }}>✕</button>
    </div>
  );
}

function formatTime12(time24) {
  if (!time24) return '';
  try {
    const [h, m] = time24.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  } catch { return time24; }
}